import React from 'react';
import { motion } from 'framer-motion';
import {
  Coins, Scale, ScrollText, ChevronRight, Calculator, BookOpen, HelpCircle, Play,
} from 'lucide-react';
import { gregorianToHijri, formatHijriDate } from '../../config/businessZakatConfig';
// (CountryLiftCard moved out of the hero — now rendered just above the
// CalculatorCard in ZakatGlobalPage so users see jurisdiction context
// immediately before the calculator frame.)

// Editorial hero with the Triple Gateway (Zakat / Faraid / Wasiyyah).
// Purely presentational — receives translation fn, navigation handlers,
// and the scroll-to-section helper as props.
//
// Props:
//   t                  — translation function
//   onStartCalculator  — () => void  (scrolls to person-types section)
//   onScrollToRulings  — () => void
//   onScrollToFaq      — () => void
//   onOpenInheritance  — () => void
//   onOpenWill         — () => void
//   onOpenWalkthrough  — () => void
//   onOpenBusiness     — () => void
export const ZakatHero = ({
  t,
  selectedLanguage,
  onStartCalculator,
  onScrollToRulings,
  onScrollToFaq,
  onOpenInheritance,
  onOpenWill,
  onOpenWalkthrough,
  onOpenBusiness,
}) => (
  <section id="hero" className="pt-16 relative overflow-hidden bg-stone-50" data-testid="zakat-hero">
    {/* Faint slow-rotating Girih watermark, off-center */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-40 -right-40 w-[680px] h-[680px] opacity-[0.07] slow-spin bg-girih bg-contain bg-no-repeat bg-center"
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-32 -left-32 w-[420px] h-[420px] opacity-[0.05] bg-girih bg-contain bg-no-repeat bg-center"
      style={{ transform: 'rotate(15deg)' }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 pb-3">
      {/* Editorial intro */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="text-center max-w-3xl mx-auto"
      >
        {/* Warm greeting + Bismillah on ONE line — saves ~24px vertical */}
        <p
          data-testid="hero-greeting"
          dir="ltr"
          className="mb-1 flex items-center justify-center gap-2 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.22em] text-[#4A5568]"
        >
          <span aria-hidden="true" className="inline-block w-3 h-px bg-[#EFE7D8]" />
          <span>As-salāmu ʿalaykum</span>
          <span aria-hidden="true" className="text-gold-600/60">·</span>
          <span className="text-[#4A5568]">{t('home.bismillah')}</span>
          <span aria-hidden="true" className="inline-block w-3 h-px bg-[#EFE7D8]" />
        </p>
        <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-[#0D1520] leading-[1.05] tracking-tight">
          {t('hero.title')}
        </h2>
        {/* Hijri date + brand mantra on ONE line — saves ~20px vertical */}
        <div
          data-testid="hero-meta"
          className="mt-1 inline-flex items-center flex-wrap justify-center gap-x-2 gap-y-0.5 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em]"
          dir="ltr"
        >
          <span data-testid="hijri-date-hero" className="text-gold-700">
            {formatHijriDate(gregorianToHijri(), selectedLanguage?.code || 'en')}
          </span>
          <span className="text-gold-600/50" aria-hidden="true">·</span>
          <span data-testid="brand-mantra" className="text-[#0D1520] tracking-[0.24em]">
            Calculate · Plan · Fulfill
          </span>
        </div>
        <div className="brass-divider w-16 mx-auto mt-1.5" />
      </motion.div>

      {/* Triple Gateway — Zakat / Faraid / Wasiyyah */}
      <div className="mt-3 md:mt-5 grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
        {/* Zakat Gateway */}
        <motion.div
          data-testid="hero-zakat-gateway"
          role="button"
          tabIndex={0}
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          onClick={onStartCalculator}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStartCalculator(); } }}
          className="group relative overflow-hidden cursor-pointer text-left rounded-2xl bg-[#EDF5F1] text-[#0D1520] p-3.5 md:p-5 border border-[#CBD2D9] shadow-editorial hover:shadow-editorial-lg hover:shadow-amber-500/20 transition-all duration-500 hover:-translate-y-1 hover:scale-[1.015] hover:border-amber-400/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-50"
        >
          <div aria-hidden="true" className="absolute -right-20 -bottom-20 w-[360px] h-[360px] opacity-[0.06] bg-girih bg-contain bg-no-repeat" />
          <div aria-hidden="true" className="absolute inset-0 bg-noise opacity-[0.25] mix-blend-overlay pointer-events-none" />

          <div className="relative">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-xl bg-gold-500/20 ring-1 ring-gold-500/40 flex items-center justify-center">
                <Coins className="w-4 h-4 text-gold-400" />
              </div>
              <span className="overline !text-gold-400 !tracking-[0.22em] !text-[10px] md:!text-[11px]">{t('home.engines.zakat.kicker')}</span>
            </div>

            <h2 className="font-heading text-xl md:text-3xl font-bold leading-[1.1] mb-1.5">
              {t('home.engines.zakat.title')}
            </h2>
            <p className="text-stone-200/85 text-xs md:text-sm leading-relaxed max-w-md line-clamp-2 md:line-clamp-none">
              {t('home.engines.zakat.desc')}
            </p>

            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {[
                t('home.engines.zakat.pPersonal'),
                t('home.engines.zakat.pBusiness'),
                t('home.engines.zakat.pLiveNisab'),
              ].map((p) => (
                <span key={p} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EFE7D8] border border-[#EDE5D6] text-stone-100">
                  {p}
                </span>
              ))}
            </div>

            <div className="mt-3 md:mt-4 flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 text-gold-400 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                <Calculator className="w-4 h-4" />
                {t('hero.startCalculator')}
                <ChevronRight className="w-4 h-4" />
              </span>
              <button
                data-testid="hero-business-link"
                onClick={(e) => { e.stopPropagation(); onOpenBusiness(); }}
                className="text-xs text-stone-300 hover:text-gold-400 transition-colors editorial-link hidden sm:inline"
              >
                {t('home.engines.zakat.bizLink')} →
              </button>
            </div>
          </div>
        </motion.div>

        {/* Faraid Gateway */}
        <motion.button
          data-testid="hero-faraid-gateway"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          onClick={onOpenInheritance}
          className="group relative overflow-hidden text-left rounded-2xl bg-lapis-900 text-[#0D1520] p-3.5 md:p-5 border border-lapis-700 shadow-editorial hover:shadow-editorial-lg hover:shadow-amber-500/25 transition-all duration-500 hover:-translate-y-1 hover:scale-[1.015] hover:border-amber-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-50"
        >
          <div aria-hidden="true" className="absolute -right-24 -top-24 w-[420px] h-[420px] opacity-[0.07] bg-girih bg-contain bg-no-repeat" />
          <div aria-hidden="true" className="absolute inset-0 bg-noise opacity-[0.25] mix-blend-overlay pointer-events-none" />

          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-xl bg-gold-500/25 ring-1 ring-gold-500/50 flex items-center justify-center">
                  <Scale className="w-4 h-4 text-gold-300" />
                </div>
                <span className="overline !text-gold-300 !tracking-[0.22em] !text-[10px] md:!text-[11px]">{t('home.engines.faraid.kicker')}</span>
              </div>
              <span className="text-[10px] font-bold tracking-[0.18em] px-2 py-0.5 rounded-full bg-gold-500 text-lapis-900">{t('home.engines.new')}</span>
            </div>

            <h2 className="font-heading text-xl md:text-3xl font-bold leading-[1.1] mb-1.5">
              {t('home.engines.faraid.title')}
            </h2>
            <p className="text-stone-200/85 text-xs md:text-sm leading-relaxed max-w-md line-clamp-2 md:line-clamp-none">
              {t('home.engines.faraid.desc')}
            </p>

            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {[
                t('home.engines.faraid.pAll'),
                t('home.engines.faraid.pTree'),
                t('home.engines.faraid.pAwl'),
              ].map((p) => (
                <span key={p} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold-500/15 border border-gold-500/40 text-gold-200">
                  {p}
                </span>
              ))}
            </div>

            <div className="mt-3 md:mt-4 flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 text-gold-300 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                <Scale className="w-4 h-4" />
                {t('home.engines.faraid.cta')}
                <ChevronRight className="w-4 h-4" />
              </span>
              <span className="text-xs text-stone-400 hidden sm:inline">{t('home.engines.faraid.tagline')}</span>
            </div>
          </div>
        </motion.button>

        {/* Wasiyyah Gateway */}
        <motion.button
          data-testid="hero-wasiyyah-gateway"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          onClick={onOpenWill}
          className="group relative overflow-hidden text-left rounded-2xl bg-[#EDF5F1] text-[#0D1520] p-3.5 md:p-5 border border-[#CBD2D9] shadow-editorial hover:shadow-editorial-lg hover:shadow-amber-500/20 transition-all duration-500 hover:-translate-y-1 hover:scale-[1.015] hover:border-amber-400/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-50"
        >
          <div aria-hidden="true" className="absolute -left-24 -bottom-24 w-[420px] h-[420px] opacity-[0.06] bg-girih bg-contain bg-no-repeat" />
          <div aria-hidden="true" className="absolute inset-0 bg-noise opacity-[0.22] mix-blend-overlay pointer-events-none" />

          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-xl bg-gold-500/20 ring-1 ring-gold-500/40 flex items-center justify-center">
                  <ScrollText className="w-4 h-4 text-gold-400" />
                </div>
                <span className="overline !text-gold-400 !tracking-[0.22em] !text-[10px] md:!text-[11px]">{t('home.engines.wasiyyah.kicker')}</span>
              </div>
              <span className="text-[10px] font-bold tracking-[0.18em] px-2 py-0.5 rounded-full bg-gold-500 text-[#0D1520]">{t('home.engines.new')}</span>
            </div>

            <h2 className="font-heading text-xl md:text-3xl font-bold leading-[1.1] mb-1.5">
              {t('home.engines.wasiyyah.title')}
            </h2>
            <p className="text-stone-200/85 text-xs md:text-sm leading-relaxed max-w-md line-clamp-2 md:line-clamp-none">
              {t('home.engines.wasiyyah.desc')}
            </p>

            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {[
                t('home.engines.wasiyyah.pCap'),
                t('home.engines.wasiyyah.pGuard'),
                t('home.engines.wasiyyah.pLetter'),
              ].map((p) => (
                <span key={p} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold-500/15 border border-gold-500/40 text-gold-200">
                  {p}
                </span>
              ))}
            </div>

            <div className="mt-3 md:mt-4 flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 text-gold-300 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                <ScrollText className="w-4 h-4" />
                {t('home.engines.wasiyyah.cta')}
                <ChevronRight className="w-4 h-4" />
              </span>
              <span className="text-xs text-stone-400 hidden sm:inline">{t('home.engines.wasiyyah.tagline')}</span>
            </div>
          </div>
        </motion.button>
      </div>

      {/* Secondary actions row — small low-key links instead of three large pill buttons (clutter reduction) */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[12px]">
        <button
          data-testid="learn-more-btn"
          onClick={onScrollToRulings}
          className="inline-flex items-center gap-1.5 text-stone-600 hover:text-[#0D1520] underline decoration-dotted underline-offset-4 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5" />
          {t('hero.learnMore')}
        </button>
        <button
          data-testid="learn-more-faq"
          onClick={onScrollToFaq}
          className="inline-flex items-center gap-1.5 text-stone-600 hover:text-[#0D1520] underline decoration-dotted underline-offset-4 transition-colors"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          {t('home.howItWorks')}
        </button>
        <button
          data-testid="hero-cta-walkthrough"
          onClick={onOpenWalkthrough}
          className="inline-flex items-center gap-1.5 text-[#0D1520] hover:text-gold-600 underline decoration-dotted underline-offset-4 transition-colors font-medium"
        >
          <Play className="w-3 h-3" />
          {t('home.walkthrough')}
        </button>
      </div>
    </div>
  </section>
);

export default ZakatHero;
